import { ISubject } from "@/Interfaces/subject";
import {
  LargeHeadingStyled,
  SectionHeaderStyled,
  SmallHeadingStyled,
} from "../../../components/custom/texts/texts.styled";
import { Box, Card, Flex, Icon } from "@nubeteck/components";
import { Button, Form, Image, Input, Layout, Modal, Select, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useState } from "react";
import {
  useGetSubjectsByStudentMutation,
  useWithdrawSubjectByStudentMutation,
} from "src/services/subjectsServices";
import toast from "react-hot-toast";
import { NoResultsSVG } from "src/assets";
import { useGetAdminPeriodsQuery, useGetStudentsMutation } from "src/services";
import { toastErrorStyle, useSuccessSubjectWithdrawnToast } from "src/constants";
import { ArrayUtils } from "@nubeteck/utils";
import { useLocale } from "../../../i18n/localeContext";
import { getWithdrawSubjectPage } from "../../../Api/content";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaAdminWithdraw";

interface ISubjectByStudent extends ISubject {
  seccionId: number;
  estudianteId: number;
}

const AdminWithdraw: React.FC = () => {
  const [modal, contextHolder] = Modal.useModal();
  const [periodoId, setPeriodoId] = React.useState();
  const [anio, setAnio] = React.useState<number>();
  const [estudiante, setEstudiante] = React.useState();
  const { locale } = useLocale();
  const [withdrawSubjectPage, setWithdrawSubjectPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError]= useState<boolean>(false);
  const [translatedData, setTranslatedData] = React.useState<{
    asignaturas: { [key: string]: string };
  }>({
    asignaturas: {},
  });
  const { data: periodos } = useGetAdminPeriodsQuery(anio);
  const [getStudents, { data: students, isLoading: isLoadingStudents }] = useGetStudentsMutation();
  const [getSubjects, { data, isLoading }] = useGetSubjectsByStudentMutation();
  const [
    withdrawSubject,
    { isLoading: isLoadingWithdraw, isSuccess: isSuccessWithdraw, error: errorWithdraw },
  ] = useWithdrawSubjectByStudentMutation();
  const showSuccessSubjectWithdrawnToast = useSuccessSubjectWithdrawnToast();

  const studentsOptions = ArrayUtils.selectLabelValue(
    students?.data?.result ?? [],
    "estudianteNombre",
    "estudianteId",
  );

  const periodosOptions = ArrayUtils.selectLabelValue(periodos?.data ?? [], "nombre", "idPeriodo");

  const filterOption = (input: string, option?: { label: string; value: string }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  useEffect(() => {
    if (estudiante && anio !== 0 && periodoId) {
      getSubjects({ estudianteId: estudiante, anio, periodoId });
    }
  }, [estudiante, anio, periodoId, getSubjects, isSuccessWithdraw]);

  React.useEffect(() => {
    if (isSuccessWithdraw) {
      showSuccessSubjectWithdrawnToast();
    }
  }, [isSuccessWithdraw, showSuccessSubjectWithdrawnToast]);

  React.useEffect(() => {
    if (errorWithdraw) {
      toast.error(
        errorWithdraw?.data?.message ?? JSON.parse(errorWithdraw?.data)?.message,
        toastErrorStyle,
      );
    }
  }, [errorWithdraw]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = data?.data?.secciones && Array.isArray(data.data.secciones) && data.data.secciones.length > 0;
      if (!hasData) {
        setTranslatedData({ asignaturas: {} });
        return;
      }

      const asignaturasTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      data.data.secciones.forEach((record: ISubjectByStudent) => {
        if (record.asignaturaNombre && !asignaturasTranslations.hasOwnProperty(record.asignaturaNombre)) {
          asignaturasTranslations[record.asignaturaNombre] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, record.asignaturaNombre, locale).then((translated) => {
              asignaturasTranslations[record.asignaturaNombre] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);
      setTranslatedData({
        asignaturas: asignaturasTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [data?.data?.secciones, locale]);

  const confirm = useCallback(
    (subject: ISubjectByStudent) => {
      modal.confirm({
        title: `¿Está seguro(a) de que desea retirar la asignatura de ${subject?.asignaturaNombre} para el estudiante ${data?.data?.nombreCompleto}`,
        icon: <Icon name="" color="#FFC53D" />,
        okText: "Aceptar",
        onOk: () => {
          withdrawSubject({ seccionId: subject?.seccionId, estudianteId: subject.estudianteId });
        },
        cancelText: "Cancelar",
        style: { top: 200 },
      });
    },
    [modal, withdrawSubject, data],
  );

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setWithdrawSubjectPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
      getWithdrawSubjectPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setWithdrawSubjectPage(data);
          setIsLoadingContent(false);
        }
      })
        .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setWithdrawSubjectPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const columns = React.useMemo<ColumnsType<ISubjectByStudent>>(
    () => [
      {
        title: withdrawSubjectPage?.withdraw_table?.[0]?.label || "Asignatura",
        dataIndex: "asignaturaNombre",
        render: (text: string) => translatedData.asignaturas[text] || text,
      },
      {
        title: withdrawSubjectPage?.withdraw_table?.[1]?.label || "Créditos",
        dataIndex: "creditos",
        width: 100,
      },
      {
        title: withdrawSubjectPage?.withdraw_table?.[2]?.label || "Sección",
        dataIndex: "asignaturaSeccionCodigo",
        width: 100,
      },
      {
        title: withdrawSubjectPage?.withdraw_table?.[3]?.label || "Profesor(a)",
        dataIndex: "profesorNombre",
      },
      {
        title: withdrawSubjectPage?.withdraw_table?.[4]?.label || "Horario",
        dataIndex: "horario",
      },
      {
        title: "",
        dataIndex: "asignaturaId",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Button type="default" onClick={() => confirm(record)}>
            Retirar asignatura
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [withdrawSubjectPage, withdrawSubjectPage?.withdraw_table, confirm, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setWithdrawSubjectPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      {isLoadingContent ? (
        <>
          <SectionHeaderStyled>
            <Skeleton.Input active size="large" style={{ width: 400, marginBottom: 16 }} />
          </SectionHeaderStyled>
          <SectionHeaderStyled style={{ gap: 5 }}>
            <Skeleton.Input active size="default" style={{ width: 200, marginRight: 8 }} />
            <Skeleton.Input active size="default" style={{ width: 50, marginRight: 8 }} />
            <Skeleton.Input active size="default" style={{ width: 150 }} />
          </SectionHeaderStyled>
          <Content style={{ marginTop: "16px" }}>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 8 }} />
            </Card>
          </Content>
        </>
      ) : (
        <>
          <SectionHeaderStyled>
            <LargeHeadingStyled>
              {data?.estudianteNombre
                ? `Asignaturas inscritas del(la) estudiante ${data?.data?.nombreCompleto} - ${data?.id}`
                : withdrawSubjectPage?.title || "Retiro de asignaturas administrativo"}
            </LargeHeadingStyled>
          </SectionHeaderStyled>
          <SectionHeaderStyled style={{ gap: 5 }}>
            <Form.Item label={withdrawSubjectPage?.student_name || "Estudiante (nombre o ID)"}>
              <Select
                options={studentsOptions}
                filterOption={filterOption}
                placeholder={withdrawSubjectPage?.select_optionA || "Búsqueda de estudiantes"}
                showSearch
                onSearch={(e) =>
                  getStudents({
                    rowsCount: 20,
                    page: 1,
                    estudianteNombre: e,
                  })
                }
                optionFilterProp="children"
                allowClear
                loading={isLoadingStudents}
                onChange={(e) => {
                  setEstudiante(e);
                }}
              />
            </Form.Item>
            <Form.Item label={withdrawSubjectPage?.year || "Año"}>
              <Input
                type="number"
                size="small"
                defaultValue={anio}
                onChange={(e) => setAnio(+e.target.value)}
                placeholder={withdrawSubjectPage?.select_optionB || "Año"}
                style={{ height: "35px", marginRight: "15px" }}
              />
            </Form.Item>
            <Form.Item label={withdrawSubjectPage?.filter || "Filtro por periodo"}>
              <Select
                style={{ minWidth: 200 }}
                options={periodosOptions}
                placeholder={withdrawSubjectPage?.select_optionC || "Seleccione un periodo"}
                onChange={(e) => {
                  setPeriodoId(e);
                }}
                allowClear
              />
            </Form.Item>
          </SectionHeaderStyled>
          <Content style={{ marginTop: "16px" }}>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<ISubjectByStudent>
                locale={{
                  emptyText: (
                    <Box>
                      <Box>
                        <SmallHeadingStyled>
                          {estudiante && anio && periodoId
                            ? "No se encontraron asignaturas inscritas"
                            : withdrawSubjectPage?.message || "Seleccione un estudiante, año y periodo para mostrar las asignaturas correspondientes"}
                        </SmallHeadingStyled>
                        <Image
                          src={NoResultsSVG}
                          preview={false}
                          alt="Ilustración sin resultados"
                          width={200}
                        />
                      </Box>
                    </Box>
                  ),
                }}
                loading={isLoading || isLoadingWithdraw}
                columns={columns}
                dataSource={data?.data?.secciones}
                rowKey={(v) => `${v.asignaturaCodigo} ${v.asignaturaNombre}`}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
              />
            </Card>
          </Content>
        </>
      )}
      {contextHolder}
    </Flex>
  );
};

export default AdminWithdraw;
